import React from "react";
import {
    MsalProvider,
    AuthenticatedTemplate,
    useMsal,
    UnauthenticatedTemplate,
} from "@azure/msal-react";
import { PageLayout } from "./components/Authorization/PageLayout";
import { loginRequest } from "./components/Authorization/authConfig";
import { Container, CssBaseline, Fab } from "@mui/material";
import TabContainer from "./components/TabContainer";
import { ThemeProvider } from "@emotion/react";
import Theme from "./Theme";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Redirect from "./pages/Redirect/Redirect";
import StatsPage from "./pages/Stats/StatsPage";

/**
 * Most applications will need to conditionally render certain components based on whether a user is signed in or not.
 * msal-react provides 2 easy ways to do this. AuthenticatedTemplate and UnauthenticatedTemplate components will
 * only render their children if a user is authenticated or unauthenticated, respectively. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const MainContent = () => {
    /**
     * useMsal is hook that returns the PublicClientApplication instance,
     * that tells you what msal is currently doing. For more, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
     */
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    const handleRedirect = () => {
        instance
            .loginRedirect({
                ...loginRequest,
                prompt: "create",
            })
            .catch((error) => console.log(error));
    };
    return (
        <>
            <AuthenticatedTemplate>
                {activeAccount ? (
                    <Container maxWidth='lg'>
                        <TabContainer />
                    </Container>
                ) : null}
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <Fab
                    variant="extended"
                    onClick={handleRedirect}
                    sx={{
                        mt: 5,
                        backgroundColor: 'secondary.main',
                        color: 'secondary.contrastText',
                        ":hover": {
                            backgroundColor: 'secondary.dark',
                            color: 'secondary.dark.contrastText',
                        },
                    }}
                >
                    Sign in with your Haskoning account
                </Fab>
            </UnauthenticatedTemplate>
        </>
    );
};

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */

const StatsContent = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    return (
        <AuthenticatedTemplate>
            {activeAccount ? (
                <Container maxWidth="lg">
                    <StatsPage />
                </Container>
            ) : null}
        </AuthenticatedTemplate>
    );
};

const App = ({ instance }) => {
    return (
        <ThemeProvider theme={Theme}>
            <CssBaseline>
                <BrowserRouter>
                    <MsalProvider instance={instance}>
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    <PageLayout>
                                        <MainContent />
                                    </PageLayout>
                                }
                            />
                            <Route
                                path="/stats"
                                element={
                                    <PageLayout>
                                        <StatsContent />
                                    </PageLayout>
                                }
                            />
                            <Route
                                path="/redirect/:uid"
                                element={
                                    <Redirect />
                                }
                            />
                        </Routes>
                    </MsalProvider>
                </BrowserRouter>
            </CssBaseline>
        </ThemeProvider>
    );
};

export default App;
